import React, { useState, useEffect, useCallback, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoSrc from "../assets/images/icon.png";
import { collection, getDocs, getDoc, doc, setDoc, serverTimestamp, query, where, updateDoc, runTransaction } from "firebase/firestore";
import { db } from "../config/Firebase";
import { useLocation, useNavigate } from "react-router-dom";

export function Sales() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const typeParam = searchParams.get("type");

  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientDetails, setPatientDetails] = useState({
    id: "",
    name: "",
    age: "",
    gender: "",
    address: "",
    acceptance: null,
  });
  const [mobile, setMobile] = useState("");
  const [orderItems, setOrderItems] = useState([]);
  const [branch, setBranch] = useState("ADMIN");
  const [adminSelectedBranch, setAdminSelectedBranch] = useState("");
  const [currentType, setCurrentType] = useState("frames");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderedQuantity, setOrderedQuantity] = useState(1);
  const [consultancyFee, setConsultancyFee] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFetchingPatients, setIsFetchingPatients] = useState(true);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(""); // NEW: Brand filter state

  // Walk-in (non-patient) customer state
  const [isWalkIn, setIsWalkIn] = useState(typeParam === "walkin");

  useEffect(() => {
    if (typeParam === "walkin" && !isWalkIn) setIsWalkIn(true);
    if (typeParam === "patient" && isWalkIn) setIsWalkIn(false);
  }, [typeParam]);

  const handleToggleWalkIn = (walkin) => {
    setIsWalkIn(walkin);
    navigate(`/Sales?type=${walkin ? 'walkin' : 'patient'}`, { replace: true });
  };

  const [walkInName, setWalkInName] = useState("");
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Billing summary state
  const [showSummary, setShowSummary] = useState(false);
  const [gstRate, setGstRate] = useState(0); // 0,5,10,16,26
  const [discountType, setDiscountType] = useState("amount"); // 'amount' | 'percent'
  const [discountValue, setDiscountValue] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");

  // Add-new-product inline form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newProductID, setNewProductID] = useState("");
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCost, setNewCost] = useState("");
  const [newQuantity, setNewQuantity] = useState(1);
  const [newStatus, setNewStatus] = useState("Available");
  // Frames-only fields
  const [newMaterial, setNewMaterial] = useState("");
  const [newFrameColor, setNewFrameColor] = useState("");
  const [newShape, setNewShape] = useState("Round");
  const [newBrand, setNewBrand] = useState("Rayban");
  // Recommendations state
  const [popularCounts, setPopularCounts] = useState({});
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const branchOptions = ["ChinnaSalem", "Thuraiyur", "Thammampatti"];

  const getBranchCode = (branchName) => {
    const codes = {
      'ChinnaSalem': 'CSM',
      'Thammampatti': 'TPT',
      'Thuraiyur': 'TYV',
    };
    return codes[branchName] || branchName.substring(0, 3).toUpperCase();
  };

  const COMPANY_INFO = {
    name: "Aram Eyecare",
    email: "aramprimaryeyecare@gmail.com",
    phone: "+91 9600481947",
    address:
      "43/9A, Moongilpadi Rd, opposite to sengunthar mini mandabam, Chinnasalem, Tamil Nadu 606201",
    tagline: "An Eye care that cares for you...",
  };

  // Types that allow decimal quantities (0.5 steps)
  const decimalAllowedTypes = ["lens", "contactlens"];
  const isDecimalAllowed = decimalAllowedTypes.includes(currentType);

  const getCollectionName = (type) => {
    switch (type) {
      case "frames": return "Local Inventory";
      case "lens": return "Lens Local";
      case "contactlens": return "ContactLens Local";
      case "sunglassess": return "sunglassess local";
      case "accessories": return "Accessories Local";
      case "storeinventory": return "Store Inventory Local";
      case "serviceaccessories": return "Service Accessories Local";
      default: return "Local Inventory";
    }
  };

  const getCategoryOptions = (type) => {
    switch (type) {
      case "frames":
        return [
          { value: "DesignerMetal_MetalFrame", label: "Designer Metal & Metal Frame" },
          { value: "FrameLess", label: "Frame Less" },
          { value: "FullFrame", label: "Full Frame" },
          { value: "HalfFrame", label: "Half Frame" },
          { value: "SafetyGlassess", label: "Safety Glasses" },
          { value: "Sunglassess", label: "Sunglasses" },
          { value: "Kids-Frame", label: "Kids Frame" },
          { value: "Kids-Sunglass", label: "Kids Sunglass" },
          { value: "Accessories", label: "Accessories" },
        ];
      case "sunglassess":
        return [
          { value: "Polarized", label: "Polarized" },
          { value: "Non-Polarized", label: "Non-Polarized" },
          { value: "UV Protection", label: "UV Protection" },
          { value: "Sports", label: "Sports" },
          { value: "Fashion", label: "Fashion" },
          { value: "Aviator", label: "Aviator" },
          { value: "Wayfarer", label: "Wayfarer" },
          { value: "Cat-Eye", label: "Cat-Eye" },
          { value: "Round", label: "Round" },
          { value: "Square", label: "Square" },
          { value: "Oversized", label: "Oversized" },
          { value: "Gradient", label: "Gradient" },
          { value: "Mirrored", label: "Mirrored" },
          { value: "Classic", label: "Classic" },
          { value: "Designer", label: "Designer" },
        ];
      case "storeinventory":
        return [
          { value: "Fluorescent strip", label: "Fluorescent strip" },
          { value: "Schirmer strip", label: "Schirmer strip" },
          { value: "Proparacaine", label: "Proparacaine" },
          { value: "Aurominde", label: "Aurominde" },
          { value: "Buds", label: "Buds" },
          { value: "Needle", label: "Needle" },
          { value: "NS", label: "NS" },
          { value: "AR roll", label: "AR roll" },
          { value: "Cloth box", label: "Cloth box" },
          { value: "Nail polish remover", label: "Nail polish remover" },
          { value: "Order book", label: "Order book" },
          { value: "Bill book", label: "Bill book" },
          { value: "Cellotape", label: "Cellotape" },
          { value: "Pen", label: "Pen" },
          { value: "Carbon paper", label: "Carbon paper" },
          { value: "Pen marker", label: "Pen marker" },
          { value: "Monitor remote battery", label: "Monitor remote battery" },
          { value: "Colin", label: "Colin" },
          { value: "Tissue paper", label: "Tissue paper" },
          { value: "Mask", label: "Mask" },
          { value: "Sanitiser", label: "Sanitiser" },
          { value: "Gloves", label: "Gloves" },
        ];
      case "serviceaccessories":
        return [
          { value: "Needle screw", label: "Needle screw" },
          { value: "Nosepad screw", label: "Nosepad screw" },
          { value: "Gen screw", label: "Gen screw" },
          { value: "Nosepad", label: "Nosepad" },
          { value: "RL nosepad", label: "RL nosepad" },
          { value: "Fix nosepad", label: "Fix nosepad" },
          { value: "RL Bush", label: "RL Bush" },
          { value: "Nose player", label: "Nose player" },
          { value: "Cutter", label: "Cutter" },
          { value: "Supra Wire", label: "Supra Wire" },
          { value: "Minus screwdriver", label: "Minus screwdriver" },
          { value: "Plus screwdriver", label: "Plus screwdriver" },
          { value: "Pin needle", label: "Pin needle" },
          { value: "Frame Strap", label: "Frame Strap" },
          { value: "3 piece remover", label: "3 piece remover" },
          { value: "3 piece player", label: "3 piece player" },
          { value: "Wax", label: "Wax" },
        ];
      case "accessories":
        return [
          { value: "Virtue lens cleaner spray", label: "Virtue lens cleaner spray" },
          { value: "Zeiss lens cleaning kit", label: "Zeiss lens cleaning kit" },
          { value: "Zeiss wipes", label: "Zeiss wipes" },
          { value: "Zeiss anti fog kit", label: "Zeiss anti fog kit" },
          { value: "Bio true 60ml & 120ml", label: "Bio true 60ml & 120ml" },
          { value: "Renu 120ml", label: "Renu 120ml" },
          { value: "Renu rewetting drops", label: "Renu rewetting drops" },
          { value: "Dewmat Gel", label: "Dewmat Gel" },
          { value: "Maycool", label: "Maycool" },
          { value: "I Tone", label: "I Tone" },
          { value: "Matmox", label: "Matmox" },
          { value: "Moxiflip LP", label: "Moxiflip LP" },
          { value: "Tobaris", label: "Tobaris" },
          { value: "CL kit", label: "CL kit" },
          { value: "CL case", label: "CL case" },
          { value: "White punk", label: "White punk" },
          { value: "Black punk", label: "Black punk" },
          { value: "Welding glass", label: "Welding glass" },
          { value: "Safety glass white", label: "Safety glass white" },
          { value: "Pocket box", label: "Pocket box" },
          { value: "Sunglass pouch", label: "Sunglass pouch" },
          { value: "Branded box", label: "Branded box" },
          { value: "Reading glass", label: "Reading glass" },
        ];
      case "contactlens":
        return [
          { value: "Power sph", label: "Power sph" },
          { value: "Power toric", label: "Power toric" },
          { value: "Cosmetic", label: "Cosmetic" },
          { value: "Prosthetics", label: "Prosthetics" },
        ];
      case "lens":
        return [
          { value: "wt-s", label: "wt-s" },
          { value: "G-s", label: "G-s" },
          { value: "B-s", label: "B-s" },
          { value: "BB-s", label: "BB-s" },
          { value: "UVB-s", label: "UVB-s" },
          { value: "GD-s", label: "GD-s" },
          { value: "Gy-P", label: "Gy-P" },
          { value: "GG-P", label: "GG-P" },
          { value: "GB-P", label: "GB-P" },
          { value: "RT-w", label: "RT-w" },
          { value: "RT-g", label: "RT-g" },
          { value: "RT-b", label: "RT-b" },
          { value: "RT-wp", label: "RT-wp" },
          { value: "RT-gp", label: "RT-gp" },
          { value: "RT-bp", label: "RT-bp" },
          { value: "MF-w", label: "MF-w" },
          { value: "MF-g", label: "MF-g" },
          { value: "MF-b", label: "MF-b" },
          { value: "MF-BB", label: "MF-BB" },
          { value: "MF-advdri", label: "MF-advdri" },
          { value: "MF-Gd", label: "MF-Gd" },
          { value: "MF-wp", label: "MF-wp" },
          { value: "MF-gp", label: "MF-gp" },
          { value: "MF-bbp", label: "MF-bbp" },
          { value: "MF-MG, MG-s", label: "MF-MG, MG-s" },
        ];
      default:
        return [];
    }
  };

  const isSimplifiedForm = () => currentType !== "frames";

  const safeValue = (value) => (value === undefined || value === null ? "" : value);

  // Fetch branch
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const username = localStorage.getItem('currentUsername');
        if (!username) {
          setBranch("ADMIN");
          return;
        }
        let branchData;
        const ldbDoc = await getDoc(doc(db, "LDB", username));
        if (ldbDoc.exists()) branchData = ldbDoc.data().branch;
        if (!branchData) {
          const lubDoc = await getDoc(doc(db, "LUB", username));
          if (lubDoc.exists()) branchData = lubDoc.data().branch;
        }
        setBranch(branchData || "ADMIN");
      } catch (err) {
        console.error("Error fetching branch:", err);
        setBranch("ADMIN");
      }
    };
    fetchBranch();
  }, []);

  // Fetch patients
  useEffect(() => {
    if (!branch) return; // Wait for branch to be fetched
    
    const fetchPatients = async () => {
      setIsFetchingPatients(true);
      try {
        const snapshot = await getDocs(collection(db, "PatientDetail"));
        const branchPatients = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out patients from other branches if not ADMIN
          if (branch !== "ADMIN" && data.branch !== branch) return;
          branchPatients.push({ id: doc.id, ...data });
        });
        
        setPatients(branchPatients);
      } catch (err) {
        setError("Error fetching patients: " + err.message);
      } finally {
        setIsFetchingPatients(false);
      }
    };
    fetchPatients();
  }, [branch]);

  // Reset selections when type changes
  useEffect(() => {
    setSelectedCategory("");
    setProducts([]);
    setSelectedProduct(null);
    setOrderedQuantity(1);
    setCategories([]);
    setCategorySearch("");
    setProductSearch("");
    setSelectedBrand(""); // NEW: Reset brand filter when type changes
    // Reset add-new form
    setShowAddForm(false);
    setNewCategory("");
    setNewProductID("");
    setNewName("");
    setNewDescription("");
    setNewCost("");
    setNewQuantity(1);
    setNewStatus("Available");
    setNewMaterial("");
    setNewFrameColor("");
    setNewShape("Round");
    setNewBrand("Rayban");
  }, [currentType]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    if (!branch) return;
    setIsFetchingCategories(true);
    try {
      const collName = getCollectionName(currentType);
      let q = query(collection(db, collName));
      const branchToQuery = branch === "ADMIN" ? adminSelectedBranch : branch;
      if (branchToQuery) {
        q = query(q, where("Branch", "==", branchToQuery));
      } else if (branch === "ADMIN") {
        // Admin must choose a branch to scope categories
        setCategories([]);
        setIsFetchingCategories(false);
        return;
      }
      const snapshot = await getDocs(q);
      const cats = [...new Set(snapshot.docs.map(d => safeValue(d.data().Category)).filter(Boolean))];
      setCategories(cats);
    } catch (err) {
      setError("Error fetching categories: " + err.message);
    } finally {
      setIsFetchingCategories(false);
    }
  }, [branch, adminSelectedBranch, currentType]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch popular products for branch/type from local orders
  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const branchToQuery = branch === "ADMIN" ? adminSelectedBranch : branch;
        if (!branchToQuery) {
          setPopularCounts({});
          return;
        }
        const branchDoc = await getDoc(doc(db, "local orders", branchToQuery));
        const counts = {};

        if (branchDoc.exists()) {
          const data = branchDoc.data();
          const orders = Array.isArray(data.orders) ? data.orders : [];

          orders.forEach((order) => {
            const items = Array.isArray(order.orderItems) ? order.orderItems : [];
            items.forEach((item) => {
              if (item && item.productId && item.type === currentType) {
                counts[item.productId] = (counts[item.productId] || 0) + (Number(item.quantity) || 1);
              }
            });
          });
        }

        setPopularCounts(counts);
      } catch (err) {
        console.warn("Popular fetch error:", err.message);
        setPopularCounts({});
      }
    };
    fetchPopular();
  }, [branch, adminSelectedBranch, currentType]);

  // Compute recommendations based on search term and popularity
  useEffect(() => {
    const q = productSearch.trim().toLowerCase();
    const scored = products.map((p) => {
      const name = safeValue(p.Name).toLowerCase();
      const pid = safeValue(p.ProductID).toLowerCase();
      const cat = safeValue(p.Category).toLowerCase();
      let score = 0;
      if (q) {
        if (name.startsWith(q)) score += 6;
        else if (name.includes(q)) score += 4;
        if (pid.startsWith(q)) score += 5;
        else if (pid.includes(q)) score += 3;
        if (cat === q) score += 2;
      } else {
        score += 1; // slight baseline
      }
      const pop = popularCounts[p.ProductID] || 0;
      score += Math.min(pop, 10);
      return { p, score };
    })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(({ p }) => p);
    setRecommendedProducts(scored);
  }, [products, productSearch, popularCounts]);

  // Filtered views for type-to-search UX
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const q = categorySearch.trim().toLowerCase();
    return categories.filter((c) => String(c).toLowerCase().includes(q));
  }, [categories, categorySearch]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by search term
    if (productSearch.trim()) {
      const q = productSearch.trim().toLowerCase();
      filtered = filtered.filter((p) => {
        const name = safeValue(p.Name).toLowerCase();
        const pid = safeValue(p.ProductID).toLowerCase();
        const cat = safeValue(p.Category).toLowerCase();
        return name.includes(q) || pid.includes(q) || cat.includes(q);
      });
    }

    // NEW: Filter by brand
    if (selectedBrand) {
      filtered = filtered.filter((p) => safeValue(p.Brand) === selectedBrand || (selectedBrand === "Unbranded" && !safeValue(p.Brand)));
    }

    return filtered;
  }, [products, productSearch, selectedBrand]);

  // NEW: Get unique brands from products
  const availableBrands = useMemo(() => {
    const brands = new Set();
    products.forEach((p) => {
      const brand = safeValue(p.Brand);
      if (brand) brands.add(brand);
      else brands.add("Unbranded");
    });
    return Array.from(brands).sort();
  }, [products]);

  const filteredSalesPatients = useMemo(() => {
    if (!patientSearchTerm.trim()) return patients;
    const term = patientSearchTerm.toLowerCase();
    return patients.filter((p) => {
      const nameMatch = safeValue(p.name).toLowerCase().includes(term);
      const phoneMatch = safeValue(p.phone).toLowerCase().includes(term);
      const mobileMatch = safeValue(p.mobile).toLowerCase().includes(term);
      const MobileMatch = safeValue(p.Mobile).toLowerCase().includes(term);
      return nameMatch || phoneMatch || mobileMatch || MobileMatch;
    });
  }, [patients, patientSearchTerm]);

  // Fetch products for selected category
  const handleCategorySelect = async (e) => {
    const cat = e.target.value;
    setSelectedCategory(cat);
    setSelectedProduct(null);
    setProducts([]);
    setProductSearch("");
    if (!cat) return;

    try {
      const collName = getCollectionName(currentType);
      let q = query(collection(db, collName), where("Category", "==", cat));
      const branchToQuery = branch === "ADMIN" ? adminSelectedBranch : branch;
      if (branchToQuery) {
        q = query(q, where("Branch", "==", branchToQuery));
      } else if (branch === "ADMIN") {
        setError("Please select a branch to view products.");
        return;
      }
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => safeValue(p.Name) && safeValue(p.ProductID));
      setProducts(list);
    } catch (err) {
      setError("Error fetching products: " + err.message);
    }
  };

  const addNewProduct = async () => {
    // Basic validation
    const qty = parseInt(newQuantity);
    if (
      !newCategory ||
      !newProductID.trim() ||
      !newName.trim() ||
      !newCost.toString().trim() ||
      isNaN(parseFloat(newCost)) ||
      parseFloat(newCost) <= 0 ||
      isNaN(qty) ||
      qty < 0
    ) {
      setError("Please fill all required fields and provide valid cost/quantity.");
      return;
    }

    if (branch === "ADMIN" && !adminSelectedBranch) {
      setError("Please select a branch for this product.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const collName = getCollectionName(currentType);
      const colRef = collection(db, collName);
      const docRef = doc(colRef, newProductID);

      const data = {
        ProductID: newProductID,
        Name: newName,
        Description: newDescription,
        Cost: parseFloat(newCost),
        Quantity: qty,
        Status: qty === 0 ? "Sold" : newStatus,
        Branch: branch === "ADMIN" ? adminSelectedBranch : branch,
        Category: newCategory,
      };

      if (!isSimplifiedForm()) {
        data.Material = newMaterial;
        data.FrameColor = newFrameColor;
        data.Shape = newShape;
        data.Brand = newBrand;
      }

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        if (snap.exists()) throw new Error("Product ID already exists.");
        tx.set(docRef, data);
      });

      alert("Product added successfully!");

      // Refresh categories & products
      setSelectedCategory(newCategory);
      await handleCategorySelect({ target: { value: newCategory } });
      // Select the newly added product
      const created = { id: newProductID, ...data };
      setSelectedProduct(created);

      // Reset add form
      setShowAddForm(false);
      setNewCategory("");
      setNewProductID("");
      setNewName("");
      setNewDescription("");
      setNewCost("");
      setNewQuantity(1);
      setNewStatus("Available");
      setNewMaterial("");
      setNewFrameColor("");
      setNewShape("Round");
      setNewBrand("Rayban");
    } catch (err) {
      setError("Database error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load patient details
  const handlePatientSelect = async (e) => {
    const pid = e.target.value;
    setSelectedPatientId(pid);
    if (!pid) {
      setPatientDetails({ id: "", name: "", age: "", gender: "", address: "", acceptance: null });
      setMobile("");
      return;
    }

    try {
      const docSnap = await getDoc(doc(db, "PatientDetail", pid));
      if (docSnap.exists()) {
        const data = docSnap.data();

        // Get acceptance data from the most recent visit
        let acceptanceData = null;
        const visits = Array.isArray(data.visits) ? data.visits : [];
        if (visits.length > 0) {
          // Get the latest visit (last item in visits array)
          const latestVisit = visits[visits.length - 1];
          acceptanceData = latestVisit.acceptance || null;
        }

        setPatientDetails({
          id: pid,
          name: safeValue(data.name) || safeValue(data.Name) || "",
          age: safeValue(data.age) || safeValue(data.Age) || "",
          gender: safeValue(data.gender) || safeValue(data.Gender) || safeValue(data.sex) || safeValue(data.Sex) || "",
          address: safeValue(data.address) || safeValue(data.Address) || "",
          acceptance: acceptanceData,
        });
        // Fetch and set mobile number from patient data
        const patientMobile = safeValue(data.phone) || safeValue(data.mobile) || safeValue(data.Mobile) || "";
        // Clean mobile number - remove non-digits and get last 10 digits
        const cleanedMobile = patientMobile.toString().replace(/\D/g, '');
        const last10Digits = cleanedMobile.length >= 10 ? cleanedMobile.slice(-10) : cleanedMobile;
        setMobile(last10Digits);
      }
    } catch (err) {
      setError("Error fetching patient: " + err.message);
    }
  };

  // Add item to cart with proper quantity validation
  const addItem = () => {
    if (!selectedProduct) {
      setError("Please select a product.");
      return;
    }

    let qty = Number(orderedQuantity);
    if (isNaN(qty) || qty <= 0) {
      setError("Please enter a valid quantity.");
      return;
    }

    // Enforce 0.5 steps for lens/contact lens
    if (isDecimalAllowed) {
      const rounded = Math.round(qty * 2) / 2;
      if (Math.abs(qty - rounded) > 0.001) {
        setError("Quantity for lens/contact lens must be in steps of 0.5 (example: 0.5, 1.0, 1.5, 2.0, ...)");
        return;
      }
      qty = rounded;
    } else {
      qty = Math.round(qty); // force whole number
    }

    const available = Number(selectedProduct.Quantity) || 0;
    if (qty > available) {
      setError(`Only ${available} available in stock.`);
      return;
    }

    const cost = Number(selectedProduct.Cost);
    if (isNaN(cost) || cost <= 0) {
      setError("Invalid product cost.");
      return;
    }

    const total = cost * qty;

    const newItem = {
      productName: safeValue(selectedProduct.Name),
      category: safeValue(selectedProduct.Category),
      productId: safeValue(selectedProduct.ProductID),
      quantity: qty,
      availableQuantity: available,
      cost,
      total,
      type: currentType,
      brand: safeValue(selectedProduct.Brand) || "Unbranded",
    };

    setOrderItems(prev => [...prev, newItem]);
    setError("");
  };

  const addConsultancyItem = () => {
    const fee = Number(consultancyFee);
    if (isNaN(fee) || fee <= 0) {
      setError("Please enter a valid consultancy fee amount.");
      return;
    }

    const newItem = {
      productId: "CONSULTANCY",
      productName: "Consultancy Fee",
      type: "consultancy",
      category: "Service",
      cost: fee,
      quantity: 1,
      total: fee,
      Brand: "-",
      Shape: "-",
      Material: "-",
      FrameColor: "-",
      Power1: "",
      Power2: "",
      Addition: ""
    };

    setOrderItems(prev => [...prev, newItem]);
    setConsultancyFee("");
    setError("");
  };

  const removeItem = (index) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const clearOrder = () => {
    setOrderItems([]);
    setSelectedCategory("");
    setProducts([]);
    setSelectedProduct(null);
    setOrderedQuantity(1);
  };

  const grandTotal = useMemo(() =>
    orderItems.reduce((sum, item) => sum + (item.total || 0), 0),
    [orderItems]);

  // Derived billing amounts
  const subtotal = grandTotal;
  const parsedDiscountValue = useMemo(() => {
    const v = parseFloat(String(discountValue).trim());
    return isNaN(v) || v < 0 ? 0 : v;
  }, [discountValue]);

  const discountAmount = useMemo(() => {
    if (discountType === "percent") {
      const pct = Math.min(parsedDiscountValue, 100);
      return (subtotal * pct) / 100;
    }
    return Math.min(parsedDiscountValue, subtotal);
  }, [discountType, parsedDiscountValue, subtotal]);

  const couponAmount = useMemo(() => {
    const amt = Number(couponInfo?.Amount || 0);
    if (isNaN(amt) || amt <= 0) return 0;
    // Cap to remaining after discount
    return Math.min(amt, Math.max(0, subtotal - discountAmount));
  }, [couponInfo, subtotal, discountAmount]);

  const taxable = useMemo(() => Math.max(0, subtotal - discountAmount - couponAmount), [subtotal, discountAmount, couponAmount]);
  const gstAmount = useMemo(() => (taxable * Number(gstRate || 0)) / 100, [taxable, gstRate]);
  const finalTotal = useMemo(() => taxable + gstAmount, [taxable, gstAmount]);
  const parsedAdvanceAmount = Number(advanceAmount || 0);
  const balanceAmount = useMemo(() => Math.max(0, finalTotal - parsedAdvanceAmount), [finalTotal, parsedAdvanceAmount]);

  const resetBillingState = () => {
    setShowSummary(false);
    setGstRate(0);
    setDiscountType("amount");
    setDiscountValue("");
    setCouponCode("");
    setCouponInfo(null);
    setCouponError("");
    setIsApplyingCoupon(false);
    setAdvanceAmount("");
  };

  const handleOpenSummary = () => {
    setError("");
    // Basic validations before opening summary
    if (isWalkIn) {
      if (!walkInName.trim()) { setError("Please enter the customer name."); return; }
    } else {
      if (!selectedPatientId) { setError("Please select a patient."); return; }
    }
    // Clean mobile number for validation
    const cleanedMobile = mobile.trim().replace(/\D/g, '');
    if (!cleanedMobile || cleanedMobile.length !== 10) { setError("Enter valid 10-digit mobile number."); return; }
    if (orderItems.length === 0) { setError("Please add at least one product."); return; }
    if (branch === "ADMIN" && !adminSelectedBranch) { setError("Please select a branch for billing."); return; }
    setShowSummary(true);
  };

  const applyCoupon = async () => {
    setCouponError("");
    const code = couponCode.trim();
    if (!code) { setCouponError("Enter coopound code."); setCouponInfo(null); return; }
    setIsApplyingCoupon(true);
    try {
      const snap = await getDoc(doc(db, "coopound local", code));
      if (!snap.exists()) {
        setCouponError("Coopound not found.");
        setCouponInfo(null);
      } else {
        const data = snap.data();
        const status = (data.Status || "active").toLowerCase();
        if (status !== "active") {
          setCouponError("Coopound is not active.");
          setCouponInfo(null);
        } else {
          setCouponInfo({ id: snap.id, ...data });
        }
      }
    } catch (e) {
      setCouponError("Error fetching coopound: " + e.message);
      setCouponInfo(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const sanitizeForFirestore = (data) => {
    const sanitized = { ...data };
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] === undefined || sanitized[key] === null) sanitized[key] = "";
      else if (Array.isArray(sanitized[key])) {
        sanitized[key] = sanitized[key].map(sanitizeForFirestore);
      }
    });
    return sanitized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Open the billing summary instead of immediate submit
    handleOpenSummary();
  };

  // Get next bill number starting from 21
  const getNextBillNumber = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "local orders"));
      let maxBillNumber = 20; // Start from 20, so next is 21

      querySnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const orders = Array.isArray(data.orders) ? data.orders : [];

        orders.forEach((order) => {
          if (order.billNumber && typeof order.billNumber === 'number') {
            maxBillNumber = Math.max(maxBillNumber, order.billNumber);
          }
        });
      });

      return maxBillNumber + 1; // Return next bill number
    } catch (err) {
      console.error("Error getting bill number:", err);
      return 21; // Default to 21 if error
    }
  };

  const handleConfirmCreateOrder = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError("");

    try {
      const billNumber = await getNextBillNumber();
      const orderData = buildOrderPayload();
      const currentBranch = branch === "ADMIN" ? adminSelectedBranch : branch;

      // Save order using runTransaction to safely append to orders array
      const savedOrder = await runTransaction(db, async (transaction) => {
        const branchDocRef = doc(db, "local orders", currentBranch);
        const branchDoc = await transaction.get(branchDocRef);

        let orders = [];
        if (branchDoc.exists()) {
          const data = branchDoc.data();
          orders = Array.isArray(data.orders) ? data.orders : [];
        }

        // Add unique ID to order and append to orders array
        const orderNow = new Date();
        const dateCode = `${orderNow.getFullYear()}${String(orderNow.getMonth() + 1).padStart(2, '0')}${String(orderNow.getDate()).padStart(2, '0')}`;
        const branchCode = getBranchCode(currentBranch);
        const seqStr = String(billNumber).padStart(3, '0');
        const orderWithId = {
          ...sanitizeForFirestore(orderData),
          billNumber: billNumber,
          orderId: `${branchCode}${dateCode}${seqStr}`,
          createdAt: new Date()
        };

        orders.push(orderWithId);

        transaction.set(branchDocRef, { orders }, { merge: true });

        // Update stock
        for (const item of orderItems) {
          // Consultancy fees have no physical stock, so skip inventory update
          if (item.type === "consultancy") continue;
          
          try {
            const coll = getCollectionName(item.type);
            const ref = doc(db, coll, item.productId);
            const newQty = Number(item.availableQuantity) - Number(item.quantity);
            transaction.update(ref, { Quantity: newQty });
          } catch (err) {
            console.error(`Stock update failed for ${item.productName}:`, err);
          }
        }

        return orderWithId;
      });

      try {
        await generateAndDownloadPDF(savedOrder);
      } catch (pdfErr) {
        console.warn("PDF generation failed:", pdfErr);
      }

      // Save walk-in customer to WalkInCustomers collection
      if (isWalkIn && walkInName.trim()) {
        try {
          const walkInRef = doc(collection(db, "WalkInCustomers"));
          await setDoc(walkInRef, {
            name: walkInName.trim(),
            mobile: mobile,
            branch: branch === "ADMIN" ? adminSelectedBranch : branch,
            orderId: savedOrder.orderId,
            createdAt: new Date(),
          });
        } catch (walkInErr) {
          console.warn("Failed to save walk-in customer:", walkInErr);
        }
      }

      alert("Order created successfully! Stock updated and invoice downloaded.");
      setSelectedPatientId("");
      setPatientDetails({ id: "", name: "", age: "", gender: "", address: "", acceptance: null });
      setMobile("");
      setWalkInName("");
      setIsWalkIn(false);
      clearOrder();
      resetBillingState();
    } catch (err) {
      setError("Error creating order: " + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };;

  const buildOrderPayload = () => ({
    isWalkIn: isWalkIn,
    patientId: isWalkIn ? "" : safeValue(patientDetails.id),
    patientName: isWalkIn ? safeValue(walkInName) : safeValue(patientDetails.name),
    age: isWalkIn ? "" : safeValue(patientDetails.age),
    gender: isWalkIn ? "" : safeValue(patientDetails.gender),
    address: isWalkIn ? "" : safeValue(patientDetails.address),
    mobile: safeValue(mobile),
    acceptance: isWalkIn ? null : (patientDetails.acceptance || null),
    orderItems: orderItems.map(sanitizeForFirestore),
    subtotal,
    discount: {
      type: discountType,
      value: parsedDiscountValue,
      amount: discountAmount,
    },
    coopound: couponInfo ? { code: couponInfo.id, amount: couponAmount } : null,
    gstRate: Number(gstRate || 0),
    gstAmount,
    total: finalTotal,
    advance: parsedAdvanceAmount,
    balance: balanceAmount,
    status: "order booked",
    branch: branch === "ADMIN" ? adminSelectedBranch : branch,
    date: new Date(),
  });

  // Helpers
  const fmtINR = (n) => {
    const num = Number(n || 0);
    return num.toFixed(2);
  };

  const generateAndDownloadPDF = async (order) => {
    const doc = new jsPDF();

    const branchToUse = branch === "ADMIN" ? adminSelectedBranch : branch;
    const now = new Date();
    const dateStr = order.date?.toDate ? order.date.toDate().toLocaleString() : now.toLocaleString();
    const orderId = order.orderId || order.mobile;
    const docId = order.id || orderId;

    // Load logo as data URL
    const imgDataUrl = await (async () => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = logoSrc;
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL("image/png");
      } catch (e) {
        console.warn("Failed loading logo for PDF:", e);
        return null;
      }
    })();

    // Header
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(10);
    doc.text(COMPANY_INFO.tagline, 38, 24);
    doc.text(`Email: ${COMPANY_INFO.email}`, 38, 30);
    doc.text(`Phone: ${COMPANY_INFO.phone}`, 38, 36);

    doc.setFontSize(12);
    doc.text(`Invoice`, 196, 16, { align: "right" });
    doc.setFontSize(10);
    doc.text(`Bill No: ${order.billNumber || '-'}`, 196, 22, { align: "right" });
    doc.text(`Date: ${dateStr}`, 196, 28, { align: "right" });
    doc.text(`Branch: ${branchToUse || '-'}`, 196, 34, { align: "right" });
    doc.text(`Order ID: ${orderId}`, 196, 40, { align: "right" });
    doc.text(`Document ID: ${docId}`, 196, 46, { align: "right" });

    // divider
    doc.setDrawColor(200);
    doc.line(14, 54, 196, 54);

    // Patient details box
    let y = 60;
    doc.setFontSize(11);
    doc.text("Patient Details", 14, y);
    y += 6;
    doc.setFontSize(10);
    doc.text(`Name: ${order.patientName || '-'}`, 14, y); y += 5;
    doc.text(`Mobile: ${order.mobile || '-'}`, 14, y); y += 5;
    doc.text(`Age/Gender: ${order.age || '-'} / ${order.gender || '-'}`, 14, y); y += 8;

    // Items table
    const rows = (order.orderItems || []).map((it) => [
      it.type || '-',
      it.productName || '-',
      it.category || '-',
      it.productId || '-',
      it.brand || 'Unbranded',
      String(it.quantity || 0),
      fmtINR(it.cost || 0),
      fmtINR(it.total || 0),
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Type", "Name", "Category", "Product ID", "Brand", "Qty", "Cost", "Total"]],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 0, 0], textColor: 255 },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
    });

    // Billing Summary Table
    const afterTableY = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : y) + 10;

    const summaryRows = [
      ["Subtotal", fmtINR(order.subtotal || 0)],
      ["Discount", `- ${fmtINR(order.discount?.amount || 0)}${order.discount?.type === 'percent' ? ` (${order.discount.value}%)` : ''}`],
      ["Coopound", `- ${fmtINR(order.coopound?.amount || 0)}${order.coopound?.code ? ` (${order.coopound.code})` : ''}`],
      ["Taxable", fmtINR((order.subtotal || 0) - (order.discount?.amount || 0) - (order.coopound?.amount || 0))],
      [`GST (${(order.gstRate || 0)}%)`, fmtINR(order.gstAmount || 0)],
      ["Total", fmtINR(order.total || 0)],
      ["Advance", fmtINR(order.advance || 0)],
      ["Balance", fmtINR(order.balance || 0)],
    ];

    autoTable(doc, {
      startY: afterTableY,
      head: [["Description", "Amount"]],
      body: summaryRows,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [0, 0, 0], textColor: 255 },
      columnStyles: { 1: { halign: "right" } },
      margin: { left: 14, right: 14 },
    });

    let sumY = doc.lastAutoTable.finalY;

    // Footer
    doc.setFontSize(9);
    doc.text("Thank you for your purchase!", 14, sumY + 6);
    doc.text("This is a computer generated invoice.", 14, sumY + 12);
    doc.text(`${COMPANY_INFO.name} • ${COMPANY_INFO.phone} • ${COMPANY_INFO.email}`, 14, sumY + 18);

    // Add second page for terms and conditions
    doc.addPage();

    // Header for Terms Page
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("TERMS AND CONDITIONS", 105, 30, { align: "center" });

    let termsY = 45;
    const leftMargin = 20;
    const rightMargin = 190;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    // Terms content with proper line breaks and formatting
    const terms = [
      {
        title: "PAYMENT TERMS:",
        content: [
          "• Full payment is required upon delivery unless otherwise agreed.",
          "• Advance payments are non-refundable except in cases of order cancellation by the company.",
          "• Balance payments must be completed within 48 hours of notification.",
          "• Credit card fees (if applicable) are the responsibility of the customer."
        ]
      },
      {
        title: "WARRANTY POLICY:",
        content: [
          "• Prescription lenses: 30-day adaptation guarantee for first-time progressive lens wearers.",
          "• Frame warranty: 1 year against manufacturing defects.",
          "• Lens warranty: 6 months against coating defects and scratches under normal use.",
          "• Warranty does not cover damage due to misuse, accidents, or normal wear and tear.",
          "• All warranty claims require original invoice and must be reported within warranty period."
        ]
      },
      {
        title: "EXCHANGE AND RETURN POLICY:",
        content: [
          "• Frame exchanges: Allowed within 14 days of delivery with receipt, subject to 20% restocking fee.",
          "• Custom prescription items are non-returnable unless there is an error in fulfillment.",
          "• Prescription remake: Free if error is on our part, customer cost if prescription changes.",
          "• Product must be in original condition for any exchange consideration."
        ]
      },
      {
        title: "DELIVERY AND FITTING:",
        content: [
          "• Standard delivery time: 7-14 business days from order confirmation.",
          "• Rush orders available for additional fee, subject to availability.",
          "• Professional fitting included with purchase for optimal comfort and vision.",
          "• Follow-up adjustments included free for 30 days after delivery."
        ]
      },
      {
        title: "PRESCRIPTION REQUIREMENTS:",
        content: [
          "• Valid prescription required for all corrective lenses (not older than 3 months).",
          "• Customer responsible for prescription accuracy - verification recommended.",
          "• We reserve the right to contact prescribing doctor for clarification if needed.",
          "• Progressive lens prescriptions require additional measurements and fitting time."
        ]
      },
      {
        title: "LIABILITY AND DISCLAIMERS:",
        content: [
          "• Our liability is limited to the replacement or repair of defective products.",
          "• We are not responsible for adaptation issues with progressive or multifocal lenses.",
          "• Customer advised to verify prescription comfort during trial period.",
          "• Any disputes are subject to local jurisdiction and applicable consumer protection laws."
        ]
      },
      {
        title: "PRIVACY AND DATA:",
        content: [
          "• Customer information is kept confidential and used only for order fulfillment.",
          "• Prescription data stored securely for future reference and warranty purposes.",
          "• Customer may request data deletion after completion of warranty periods.",
          "• We comply with applicable privacy and data protection regulations."
        ]
      }
    ];

    // Add each section
    terms.forEach(section => {
      // Add section title
      doc.setFont("helvetica", "bold");
      doc.text(section.title, leftMargin, termsY);
      termsY += 8;

      // Add section content
      doc.setFont("helvetica", "normal");
      section.content.forEach(line => {
        const splitText = doc.splitTextToSize(line, rightMargin - leftMargin);
        doc.text(splitText, leftMargin, termsY);
        termsY += splitText.length * 5;
      });

      termsY += 5; // Extra space between sections

      // Check if we need a new page
      if (termsY > 250) {
        doc.addPage();
        termsY = 30;
      }
    });

    // Add third page for patient examination details
    doc.addPage();

    // Header for third page
    if (imgDataUrl) {
      doc.addImage(imgDataUrl, "PNG", 14, 10, 20, 20);
    }
    doc.setFontSize(16);
    doc.text(COMPANY_INFO.name, 38, 18);
    doc.setFontSize(12);
    doc.text("Patient Examination Details", 38, 28);

    doc.setFontSize(10);
    doc.text(`Date: ${dateStr}`, 196, 16, { align: "right" });
    doc.text(`Order ID: ${orderId}`, 196, 22, { align: "right" });

    // Divider
    doc.setDrawColor(200);
    doc.line(14, 40, 196, 40);

    // Patient details section
    let pageY = 50;
    doc.setFontSize(12);
    doc.text("Patient Information", 14, pageY);
    pageY += 8;

    doc.setFontSize(10);
    doc.text(`Name: ${order.patientName || '-'}`, 14, pageY);
    pageY += 10;

    // Frame & Lens Details (auto-fetched from order items)
    const frameItems = (order.orderItems || []).filter(item => item.type === 'frames' || item.type === 'sunglassess');
    const lensItems = (order.orderItems || []).filter(item => item.type === 'lens' || item.type === 'contactlens');

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Frame & Lens Details", 14, pageY);
    doc.setFont("helvetica", "normal");
    pageY += 8;

    const frameLensRows = [];
    if (frameItems.length > 0) {
      frameItems.forEach(item => {
        frameLensRows.push(["Frame", item.productName || '-', item.category || '-', item.brand || '-']);
      });
    } else {
      frameLensRows.push(["Frame", "-", "-", "-"]);
    }
    if (lensItems.length > 0) {
      lensItems.forEach(item => {
        frameLensRows.push(["Lens", item.productName || '-', item.category || '-', item.brand || '-']);
      });
    } else {
      frameLensRows.push(["Lens", "-", "-", "-"]);
    }

    autoTable(doc, {
      startY: pageY,
      head: [["Type", "Product Name", "Category", "Brand"]],
      body: frameLensRows,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: {
        0: { fontStyle: "bold", halign: "center" },
      },
      tableWidth: 'auto',
      margin: { left: 14, right: 14 }
    });

    pageY = doc.lastAutoTable.finalY + 15;

    // Acceptance table
    doc.setFontSize(12);
    doc.text("Acceptance", 14, pageY);
    pageY += 8;

    const acceptanceData = order.acceptance && typeof order.acceptance === 'object' ? order.acceptance : {};
    const acceptanceRows = [
      [
        "OD",
        (acceptanceData.od && acceptanceData.od.sph) ? String(acceptanceData.od.sph) : "-",
        (acceptanceData.od && acceptanceData.od.cyl) ? String(acceptanceData.od.cyl) : "-",
        (acceptanceData.od && acceptanceData.od.a) ? String(acceptanceData.od.a) : "-",
        (acceptanceData.od && acceptanceData.od.bcva) ? String(acceptanceData.od.bcva) : "-",
      ],
      [
        "OS",
        (acceptanceData.os && acceptanceData.os.sph) ? String(acceptanceData.os.sph) : "-",
        (acceptanceData.os && acceptanceData.os.cyl) ? String(acceptanceData.os.cyl) : "-",
        (acceptanceData.os && acceptanceData.os.a) ? String(acceptanceData.os.a) : "-",
        (acceptanceData.os && acceptanceData.os.bcva) ? String(acceptanceData.os.bcva) : "-",
      ],
      [
        "Add",
        (acceptanceData.add) ? String(acceptanceData.add) : "-",
        "",
        "",
        "",
      ]
    ];

    autoTable(doc, {
      startY: pageY,
      head: [["", "Sph", "Cyl", "A", "BCVA"]],
      body: acceptanceRows,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      columnStyles: {
        0: { fontStyle: "bold", halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      tableWidth: 'auto',
      margin: { left: 14, right: 14 }
    });

    // Measurement Form
    const measureY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Measurements", 14, measureY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const formY1 = measureY + 10;
    doc.text("PD: ____________    A: ____________    B: ____________", 14, formY1);
    const formY2 = formY1 + 10;
    doc.text("DBL: ____________    Fr Dig: ____________    Fh: ____________", 14, formY2);

    doc.save(`Invoice_${orderId}_${now.getTime()}.pdf`);
  };

  return (
    <div className="max-w-6xl mx-auto my-10 p-8 bg-white rounded-xl shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Sales / Billing</h1>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-6 text-center">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Details */}
        <div className="border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Customer Details</h2>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${!isWalkIn ? 'text-black' : 'text-gray-400'}`}>Patient</span>
              <button
                type="button"
                onClick={() => {
                  handleToggleWalkIn(!isWalkIn);
                  setSelectedPatientId("");
                  setPatientSearchTerm("");
                  setPatientDetails({ id: "", name: "", age: "", gender: "", address: "", acceptance: null });
                  setMobile("");
                  setWalkInName("");
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isWalkIn ? 'bg-black' : 'bg-gray-300'
                  }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isWalkIn ? 'translate-x-6' : 'translate-x-1'
                  }`} />
              </button>
              <span className={`text-sm font-medium ${isWalkIn ? 'text-black' : 'text-gray-400'}`}>Walk-in</span>
            </div>
          </div>

          {isWalkIn ? (
            /* Walk-in Customer Form */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={walkInName}
                  onChange={(e) => setWalkInName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Mobile No. *</label>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter 10-digit mobile"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  required
                />
              </div>
            </div>
          ) : (
            /* Patient Selection Form */
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="relative">
                  <label className="block text-gray-700 font-medium mb-1">Select Patient</label>
                  <input
                    type="text"
                    placeholder={isFetchingPatients ? "Loading patients..." : "Search by name or phone..."}
                    value={patientSearchTerm}
                    disabled={isFetchingPatients}
                    onChange={(e) => {
                      setPatientSearchTerm(e.target.value);
                      setShowPatientDropdown(true);
                      if (!e.target.value) {
                        handlePatientSelect({ target: { value: "" } });
                      }
                    }}
                    onFocus={() => setShowPatientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPatientDropdown(false), 200)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
                  />
                  {showPatientDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredSalesPatients.length > 0 ? (
                        filteredSalesPatients.map(p => (
                          <div
                            key={p.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                            onClick={() => {
                              setPatientSearchTerm(safeValue(p.name) || `Patient ${p.id}`);
                              handlePatientSelect({ target: { value: p.id } });
                              setShowPatientDropdown(false);
                            }}
                          >
                            <div className="font-semibold text-black">{safeValue(p.name) || `Patient ${p.id}`}</div>
                            <div className="text-xs text-gray-500">
                              Phone: {safeValue(p.phone) || safeValue(p.mobile) || safeValue(p.Mobile) || "N/A"} | ID: {p.id}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">No patients found.</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Mobile No.</label>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 10-digit mobile"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    required
                  />
                </div>
              </div>

              {selectedPatientId && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-1">Name</label>
                    <input type="text" value={patientDetails.name} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-1">Age</label>
                    <input type="text" value={patientDetails.age} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-1">Gender</label>
                    <input type="text" value={patientDetails.gender || "Not specified"} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-medium mb-1">Address</label>
                    <textarea value={patientDetails.address} readOnly rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Products Section */}
        <div className="border p-6 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4">Products Details</h2>

          {/* Branch selection for Admin */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Branch</label>
              {branch === "ADMIN" ? (
                <select
                  value={adminSelectedBranch}
                  onChange={(e) => {
                    setAdminSelectedBranch(e.target.value);
                    // Reset selections when branch changes
                    setSelectedCategory("");
                    setProducts([]);
                    setSelectedProduct(null);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value="">Select branch to view products</option>
                  {branchOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={branch}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              )}
            </div>
          </div>

          {/* Type Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {[
              { key: "frames", label: "Frames" },
              { key: "lens", label: "Lens" },
              { key: "contactlens", label: "Contact Lens" },
              { key: "sunglassess", label: "Sunglasses" },
              { key: "accessories", label: "Accessories" },
              { key: "storeinventory", label: "Store Inventory" },
              { key: "serviceaccessories", label: "Service Accessories" },
              { key: "consultancy", label: "Consultancy" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setCurrentType(tab.key)}
                className={`px-4 py-2 rounded-lg font-semibold transition ${currentType === tab.key
                  ? "bg-black text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {currentType === "consultancy" ? (
            <div className="border border-blue-200 bg-blue-50 p-6 rounded-lg mb-6 shadow-sm">
              <h3 className="text-xl font-bold text-blue-900 mb-4">Consultancy Fee</h3>
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-blue-800 font-medium mb-1">Fee Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={consultancyFee}
                    onChange={(e) => setConsultancyFee(e.target.value)}
                    placeholder="Enter consultancy fee..."
                    className="w-full px-4 py-3 border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={addConsultancyItem}
                  disabled={!consultancyFee || Number(consultancyFee) <= 0}
                  className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition disabled:opacity-50 shadow-md"
                >
                  Add Fee
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Category ({currentType})</label>
              <input
                type="text"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder="Type to search category"
                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
              <select
                value={selectedCategory}
                onChange={handleCategorySelect}
                disabled={isFetchingCategories}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50"
              >
                <option value="">
                  {isFetchingCategories ? "Loading categories..." : "Select Category..."}
                </option>
                {filteredCategories.length === 0 ? (
                  <option value="" disabled>No matching categories</option>
                ) : (
                  filteredCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">Showing {filteredCategories.length} of {categories.length} categories</p>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">🏷️ Brand (Optional Filter)</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="">All Brands</option>
                {availableBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">{availableBrands.length} brands available</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Product</label>
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search by name, ID or category"
                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
              <select
                value={selectedProduct ? selectedProduct.id : ""}
                onChange={(e) => {
                  const prodId = e.target.value;
                  const product = products.find((p) => p.id === prodId);
                  setSelectedProduct(product || null);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="">Select Product...</option>
                {filteredProducts.length === 0 ? (
                  <option value="" disabled>No matching products</option>
                ) : (
                  filteredProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {safeValue(product.Name)} (ID: {safeValue(product.ProductID)}, Qty: {product.Quantity || 0}, Cost: ₹{safeValue(product.Cost)})
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">Showing {filteredProducts.length} of {products.length} products</p>
              {recommendedProducts.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Recommended</p>
                  <div className="flex flex-wrap gap-2">
                    {recommendedProducts.map((rp) => (
                      <button
                        key={rp.id}
                        type="button"
                        onClick={() => setSelectedProduct(rp)}
                        className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-xs hover:bg-gray-300"
                        title={`ID: ${safeValue(rp.ProductID)} | Qty: ${rp.Quantity || 0}`}
                      >
                        {safeValue(rp.Name)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="px-3 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800"
                >
                  Add New {currentType === "frames" ? "Frame" : "Product"}
                </button>
              </div>
            </div>
          </div>

          {showAddForm && (
            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
              <h3 className="text-lg font-semibold mb-3">Add New {currentType === "frames" ? "Frame" : "Product"}</h3>
              {error && (
                <div className="bg-red-100 text-red-700 px-3 py-2 rounded mb-3 text-center">{error}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Category</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  >
                    <option value="">Select Category</option>
                    {getCategoryOptions(currentType).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Branch</label>
                  {branch === "ADMIN" ? (
                    <select
                      value={adminSelectedBranch}
                      onChange={(e) => setAdminSelectedBranch(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <option value="">Select a branch</option>
                      {branchOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={branch} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-1">Product ID</label>
                  <input
                    type="text"
                    value={newProductID}
                    onChange={(e) => setNewProductID(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Name</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>

                {!isSimplifiedForm() && (
                  <>
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Material</label>
                      <input
                        type="text"
                        value={newMaterial}
                        onChange={(e) => setNewMaterial(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Frame Color</label>
                      <input
                        type="text"
                        value={newFrameColor}
                        onChange={(e) => setNewFrameColor(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Shape</label>
                      <select
                        value={newShape}
                        onChange={(e) => setNewShape(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <option>Round</option>
                        <option>Oval</option>
                        <option>Square</option>
                        <option>Rectangular</option>
                        <option>Cat-Eye</option>
                        <option>Butterfly</option>
                        <option>Hexagonal</option>
                        <option>Aviator</option>
                        <option>Wayfarer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Brand</label>
                      <select
                        value={newBrand}
                        onChange={(e) => setNewBrand(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <option>Generic</option>
                        <option>Rayban</option>
                        <option>Nova</option>
                        <option>Carrera</option>
                        <option>Police</option>
                        <option>Boss</option>
                        <option>Mont blanc</option>
                        <option>Tom ford</option>
                        <option>Vogue</option>
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-gray-700 font-medium mb-1">Cost (₹)</label>
                  <input
                    type="number"
                    value={newCost}
                    onChange={(e) => setNewCost(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Quantity</label>
                  <input
                    type="number"
                    min="0"
                    value={newQuantity}
                    onChange={(e) => setNewQuantity(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={parseInt(newQuantity) === 0}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option>Available</option>
                    <option>Sold</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-1">Description</label>
                  <textarea
                    rows={3}
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={addNewProduct}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                >
                  {isLoading ? "Saving..." : "Save Product"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setError("");
                  }}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {selectedProduct && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-gray-50 p-4 rounded">
              <p><strong>Name:</strong> {safeValue(selectedProduct.Name)}</p>
              <p><strong>Category:</strong> {safeValue(selectedProduct.Category)}</p>
              <p><strong>Product ID:</strong> {safeValue(selectedProduct.ProductID)}</p>
              <p><strong>🏷️ Brand:</strong> {safeValue(selectedProduct.Brand) || "Unbranded"}</p>
              <p><strong>Available Qty:</strong> {selectedProduct.Quantity || 0}</p>
              <p><strong>Cost:</strong> ₹{safeValue(selectedProduct.Cost)}</p>

              <div className="md:col-span-2">
                <label className="block text-gray-700 font-medium mb-1">
                  Order Quantity {isDecimalAllowed ? "(steps of 0.5)" : ""}
                </label>
                <input
                  type="number"
                  min={isDecimalAllowed ? "0.5" : "1"}
                  max={selectedProduct.Quantity || 1}
                  step={isDecimalAllowed ? "0.5" : "1"}
                  value={orderedQuantity}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || val === ".") {
                      setOrderedQuantity(val);
                      return;
                    }
                    const num = Number(val);
                    if (!isNaN(num)) {
                      setOrderedQuantity(num);
                    }
                  }}
                  onBlur={(e) => {
                    let val = Number(e.target.value);
                    if (isNaN(val) || val < (isDecimalAllowed ? 0.5 : 1)) {
                      val = isDecimalAllowed ? 0.5 : 1;
                    }

                    if (isDecimalAllowed) {
                      // Round to nearest 0.5
                      val = Math.round(val * 2) / 2;
                    } else {
                      val = Math.round(val);
                    }

                    if (val > (selectedProduct.Quantity || 1)) {
                      val = selectedProduct.Quantity || 1;
                    }

                    setOrderedQuantity(val);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={addItem}
              disabled={!selectedProduct || orderedQuantity <= 0}
              className="px-6 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
            >
              Add Product
            </button>
          </div>
          </>
          )}

          {orderItems.length > 0 && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={clearOrder}
                className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition"
              >
                Clear Entire Order
              </button>
            </div>
          )}

          {orderItems.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="text-sm text-gray-600 mb-2">
                Selected Items ({orderItems.length}) - Lens/Contact Lens allow 0.5 step quantities
              </p>
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 border-b text-left">Type</th>
                    <th className="px-4 py-2 border-b text-left">Product Name</th>
                    <th className="px-4 py-2 border-b text-left">Category</th>
                    <th className="px-4 py-2 border-b text-left">Product ID</th>
                    <th className="px-4 py-2 border-b text-left">🏷️ Brand</th>
                    <th className="px-4 py-2 border-b text-left">Qty</th>
                    <th className="px-4 py-2 border-b text-left">Cost (₹)</th>
                    <th className="px-4 py-2 border-b text-left">Total (₹)</th>
                    <th className="px-4 py-2 border-b text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border-b">{item.type || "N/A"}</td>
                      <td className="px-4 py-2 border-b">{safeValue(item.productName)}</td>
                      <td className="px-4 py-2 border-b">{safeValue(item.category)}</td>
                      <td className="px-4 py-2 border-b">{safeValue(item.productId)}</td>
                      <td className="px-4 py-2 border-b">{item.brand || "Unbranded"}</td>
                      <td className="px-4 py-2 border-b">{item.quantity}</td>
                      <td className="px-4 py-2 border-b">₹{(item.cost || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 border-b">₹{(item.total || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 border-b">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-semibold">
                    <td colSpan="6" className="px-4 py-2 text-right">Grand Total:</td>
                    <td className="px-4 py-2">₹{grandTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleOpenSummary}
            disabled={isLoading || orderItems.length === 0}
            className="px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Proceed to Pay (₹{grandTotal.toFixed(2)})
          </button>
        </div>
      </form>

      {/* Billing Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 sm:p-6">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 shrink-0">
              <h3 className="text-xl font-semibold">Billing Summary</h3>
              <button onClick={() => setShowSummary(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {/* Patient & Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600 mb-1">Patient</p>
                <p className="font-medium">{patientDetails.name}</p>
                <p className="text-sm">Mobile: {mobile}</p>
                <p className="text-sm">Age/Gender: {patientDetails.age || '-'} / {patientDetails.gender || '-'}</p>
                <p className="text-sm">Branch: {branch === 'ADMIN' ? adminSelectedBranch : branch}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600 mb-1">Items</p>
                <p className="text-sm">Count: {orderItems.length}</p>
                <p className="text-sm">Subtotal: ₹{subtotal.toFixed(2)}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-1">GST</label>
                <select
                  value={gstRate}
                  onChange={(e) => setGstRate(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value={0}>None (0%)</option>
                  <option value={5}>5%</option>
                  <option value={12}>12%</option>
                  <option value={18}>18%</option>
                  <option value={28}>28%</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Discount</label>
                <div className="flex gap-2">
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    className="px-3 py-2 border rounded"
                  >
                    <option value="amount">Amount (₹)</option>
                    <option value="percent">Percent (%)</option>
                  </select>
                  <input
                    type="number"
                    value={discountValue}
                    min="0"
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    placeholder={discountType === 'percent' ? 'e.g., 10' : 'e.g., 250'}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Discount applies before GST.</p>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Coopound</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Enter code"
                    disabled={!!couponInfo}
                  />
                  {couponInfo ? (
                    <button type="button" onClick={() => { setCouponInfo(null); setCouponCode(""); setCouponError(""); }} className="px-3 py-2 bg-gray-600 text-white rounded">Remove</button>
                  ) : (
                    <button type="button" onClick={applyCoupon} disabled={isApplyingCoupon} className="px-3 py-2 bg-black text-white rounded">{isApplyingCoupon ? 'Applying...' : 'Apply'}</button>
                  )}
                </div>
                {couponError && <p className="text-xs text-red-600 mt-1">{couponError}</p>}
                {couponInfo && <p className="text-xs text-green-700 mt-1">Applied: {couponInfo.id} (₹{Number(couponInfo.Amount || 0)})</p>}
              </div>
            </div>

            {/* Advance Payment */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">Advance Payment</label>
              <input
                type="number"
                value={advanceAmount}
                min="0"
                max={finalTotal}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter advance amount"
              />
              <p className="text-xs text-gray-500 mt-1">Enter amount paid in advance (optional)</p>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-4 rounded mb-4">
              <div className="flex justify-between text-sm mb-1"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm mb-1"><span>Discount</span><span>- ₹{discountAmount.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm mb-1"><span>Coopound</span><span>- ₹{couponAmount.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm mb-1"><span>Taxable</span><span>₹{taxable.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm mb-1"><span>GST ({Number(gstRate || 0)}%)</span><span>₹{gstAmount.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-lg mt-2 border-t pt-2"><span>Total</span><span>₹{finalTotal.toFixed(2)}</span></div>
              {parsedAdvanceAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm mb-1 text-blue-600"><span>Advance Paid</span><span>₹{parsedAdvanceAmount.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold text-lg text-red-600"><span>Balance Due</span><span>₹{balanceAmount.toFixed(2)}</span></div>
                </>
              )}
            </div>

            </div>
            
            <div className="p-4 sm:p-5 border-t border-gray-200 flex flex-wrap justify-end gap-3 shrink-0 rounded-b-lg bg-gray-50">
              <button type="button" onClick={() => setShowSummary(false)} className="px-5 py-2 bg-gray-500 text-white font-medium rounded hover:bg-gray-600 transition">Back</button>
              <button
                type="button"
                onClick={() => {
                  try {
                    const preview = { ...buildOrderPayload(), date: undefined };
                    generateAndDownloadPDF(preview);
                  } catch (e) { console.warn('PDF preview failed', e); }
                }}
                className="px-5 py-2 bg-gray-700 text-white rounded"
              >
                Download PDF
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateOrder}
                disabled={isLoading}
                className="px-5 py-2 bg-black text-white rounded disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Proceed to Pay & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sales;